/*
 * Raanaas Utleie – plantegninger i pop-up (sections/plantegninger.liquid).
 * Leser dataene (generert fra eierens manifest) fra <script type="application/json">, bygger størrelseslisten og
 * viser ett bilde om gangen. Bildet hentes først når pop-upen åpnes eller kunden velger noe nytt.
 * Uten skriptet (eller uten <dialog>-støtte) er knappen en vanlig lenke til bildet for standardvalget.
 */
if (!customElements.get('plantegninger-velger')) {
  const TYPENAVN = { quickup: 'Quick-Up', topptelt: 'Topptelt' };
  const GRUPPER = { quickup: 'Quick-Up telt', topptelt: 'Topptelt' };

  class PlantegningerVelger extends HTMLElement {
    connectedCallback() {
      if (this.klar) return;
      const kilde = this.querySelector('[data-plantegninger-data]');
      const lenke = this.querySelector('[data-plantegninger-apne]');
      this.dialog = this.querySelector('[data-plantegninger-dialog]');
      if (!kilde || !lenke || !this.dialog || typeof this.dialog.showModal !== 'function') return;

      let data;
      try {
        data = JSON.parse(kilde.textContent);
      } catch (e) {
        return;
      }

      // Én oppføring per størrelse med de tre oppsettene, i datafilens rekkefølge (samme som tabellene på sidene).
      const alle = [];
      for (const e of data) {
        let s = alle.find((x) => x.key === e.key);
        if (!s) {
          s = { key: e.key, type: e.type, size: e.size, oppsett: {} };
          alle.push(s);
        }
        s.oppsett[e.layout] = e;
      }
      const onsket = (this.dataset.storrelser || '').split(',').filter(Boolean);
      const valgte = onsket.length ? alle.filter((x) => onsket.includes(x.key)) : alle;
      if (!valgte.length) return;

      this.select = this.querySelector('[data-plantegninger-storrelse]');
      this.forrige = this.querySelector('[data-plantegninger-forrige]');
      this.neste = this.querySelector('[data-plantegninger-neste]');
      this.bryterne = [...this.querySelectorAll('[data-oppsett]')];
      this.antall = this.querySelector('[data-plantegninger-antall]');
      this.bilde = this.querySelector('[data-plantegninger-bilde]');
      this.full = this.querySelector('[data-plantegninger-full]');

      // Størrelseslisten, gruppert på Quick-Up og topptelt.
      for (const type of Object.keys(GRUPPER)) {
        const liste = valgte.filter((x) => x.type === type);
        if (!liste.length) continue;
        const gruppe = document.createElement('optgroup');
        gruppe.label = GRUPPER[type];
        for (const s of liste) gruppe.append(new Option(`${TYPENAVN[type]} ${s.size}`, s.key));
        this.select.append(gruppe);
      }
      this.storrelser = [...this.select.options].map((o) => valgte.find((x) => x.key === o.value));
      this.indeks = Math.max(0, this.storrelser.findIndex((x) => x.key === this.dataset.standard));
      this.oppsett = 'runde';
      if (this.storrelser.length < 2) {
        this.forrige.hidden = true;
        this.neste.hidden = true;
      }

      // Lenken byttes med en ekte knapp, så den oppfører seg som en knapp for tastatur og skjermlesere.
      this.knapp = document.createElement('button');
      this.knapp.type = 'button';
      this.knapp.className = lenke.className;
      this.knapp.textContent = lenke.textContent.trim();
      this.knapp.setAttribute('aria-haspopup', 'dialog');
      lenke.replaceWith(this.knapp);

      this.knapp.addEventListener('click', () => this.apne());
      this.querySelector('[data-plantegninger-lukk]').addEventListener('click', () => this.dialog.close());
      // Klikk på den mørke bakgrunnen lukker. Rammen inni fyller hele dialogen, så bare bakgrunnen treffer selve dialogen.
      this.dialog.addEventListener('click', (ev) => {
        if (ev.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) this.dialog.close();
      });
      // Esc lukker via nettleserens egen «cancel». Fokus går tilbake til knappen.
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
        this.knapp.focus();
      });
      this.select.addEventListener('change', () => {
        this.indeks = this.select.selectedIndex;
        this.vis();
      });
      this.forrige.addEventListener('click', () => this.flytt(-1));
      this.neste.addEventListener('click', () => this.flytt(1));
      for (const b of this.bryterne) {
        b.addEventListener('click', () => {
          this.oppsett = b.dataset.oppsett;
          this.vis();
        });
      }
      // Trykk på bildet åpner det i full størrelse, som lenken under bildet.
      this.bilde.addEventListener('click', () => {
        if (this.bilde.src) window.open(this.bilde.src, '_blank', 'noopener');
      });
      this.klar = true;
    }

    disconnectedCallback() {
      if (this.dialog?.open) document.body.classList.remove('overflow-hidden');
    }

    // Fokus på «Lukk», ellers kan nettleseren gi fokus til rammen (den ruller når bildet er høyt).
    apne() {
      this.vis();
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      this.querySelector('[data-plantegninger-lukk]').focus();
    }

    // Forrige/neste går rundt, så knappene aldri blir deaktivert og mister fokus.
    flytt(steg) {
      const n = this.storrelser.length;
      this.indeks = (this.indeks + steg + n) % n;
      this.vis();
    }

    vis() {
      const s = this.storrelser[this.indeks];
      const e = s.oppsett[this.oppsett];
      this.select.selectedIndex = this.indeks;
      for (const b of this.bryterne) {
        const valgt = b.dataset.oppsett === this.oppsett;
        b.setAttribute('aria-pressed', String(valgt));
        b.classList.toggle('button--secondary', !valgt);
      }
      const tall = document.createElement('strong');
      tall.textContent = `${e.persons} personer`;
      this.antall.replaceChildren(`${TYPENAVN[s.type]} ${s.size} med ${e.layout_label.toLowerCase()}: `, tall);
      this.full.href = e.src;
      this.lastBilde(e);
    }

    // Første bilde settes rett inn (width og height holder av plassen). Ved bytte står det gamle bildet til det nye
    // er hentet og klart, så bildet ikke blinker eller hopper.
    lastBilde(e) {
      this.onsket = e.src;
      const settInn = () => {
        if (this.onsket !== e.src) return;
        this.bilde.width = e.width;
        this.bilde.height = e.height;
        this.bilde.alt = e.alt;
        this.bilde.src = e.src;
      };
      if (!this.bilde.getAttribute('src')) return settInn();
      const neste = new Image();
      neste.src = e.src;
      (neste.decode ? neste.decode() : Promise.resolve()).then(settInn, settInn);
    }
  }

  customElements.define('plantegninger-velger', PlantegningerVelger);
}
